import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown } from 'antd';

import { RecordTitle } from '../common/RecordBadge';
import { CHEVRON_RIGHT, KEBAB } from '../icons';
import { formatCount, joinMeta } from '../../utils/counts';
import { formatShortDate } from '../../utils/dateDisplay';

/**
 * A parent collection's home: its sub-collections as compact rows — the
 * collection-card-row shape the Cards section uses — each the
 * sub-collection's plain title opening its home, an activity line, and a
 * chevron that is the same door (docs/plans/sub-collections.md §5.3).
 *
 *   Health        4 cards · 212 items · last added Sep 20   ›
 *
 * `subCollections` are `utils/collectionFamily.subCollectionsOf` summaries
 * (their own card ids, the item count in the caller's read scope);
 * `lastAddedById` maps a sub-collection id to its newest item's time, read
 * off the parent's counts rows (null when the API sends none).
 *
 * `menuFor(sub)` — collection settings' Sub-collections tab — returns a
 * row's `buildActionMenu` menu, and the row ends in that ⋯ instead of the
 * chevron (the title stays the door to its home).
 */
export default function SubCollectionList({ subCollections = [], lastAddedById = null, menuFor = null }) {
	if (!subCollections.length) return null;
	return (
		<ul className="collection-card-list sub-collection-list">
			{subCollections.map(sub => {
				const home = `/collections/${sub._id}/`;
				const menu = menuFor ? menuFor(sub) : null;
				const lastAdded = lastAddedById ? lastAddedById.get(sub._id) : null;
				const activity = joinMeta(
					formatCount(sub.cardIds?.size, 'card'),
					formatCount(sub.itemCount, 'item'),
					lastAdded ? `last added ${formatShortDate(lastAdded)}` : null,
					sub.status === 'archived' ? 'Archived' : null,
				);
				return (
					<li key={sub._id} className="collection-card-row sub-collection-row" data-collection-id={sub._id}>
						<div className="collection-card-row__line">
							<RecordTitle
								kind="collection"
								record={sub}
								title={sub.name}
								size="sm"
								to={home}
								className="collection-card-row__title"
							/>
							<span className="collection-card-row__activity meta-line">{activity}</span>
							{menu ? (
								<Dropdown trigger={['click']} placement="bottomRight" menu={menu}>
									<Button icon={<KEBAB />} aria-label={`Actions for ${sub.name}`} className="sub-collection-row__menu" />
								</Dropdown>
							) : menuFor ? null : (
								<Link to={home} className="sub-collection-row__go" aria-label={`Open ${sub.name}`}>
									<CHEVRON_RIGHT />
								</Link>
							)}
						</div>
					</li>
				);
			})}
		</ul>
	);
}
